import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { TOW_STATUSES, STATUS_LABELS, STATUS_COLORS, CITIES } from "@/lib/constants";
import Shell from "@/components/Shell";
import MapView from "@/components/MapView";
import StatusStepper from "@/components/StatusStepper";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Truck, MapPin, Send, Clock, TrendingUp, Navigation } from "lucide-react";
import ChatBox from "@/components/ChatBox";
import { toast } from "sonner";
import { PartnerEarnings } from "@/components/PartnerEarnings";
import { ChatAttachment } from "@/components/ChatAttachment";

export default function TowDashboard() {
  const { user } = useAuth();
  const [tows, setTows] = useState([]);
  const [offersByTow, setOffersByTow] = useState({});
  const [active, setActive] = useState(null);
  const [cityFilter, setCityFilter] = useState("");
  const [of, setOf] = useState({ price: "", eta: "", tow_type: user?.tow_type || "عادية", driver_name: user?.name || "", driver_phone: user?.phone || "" });

  const load = async () => {
    const { data } = await api.get("/tow-requests");
    setTows(data);
    const entries = await Promise.all(data.map(async (t) => {
      const o = await api.get(`/tow-requests/${t.id}/offers`);
      return [t.id, o.data];
    }));
    setOffersByTow(Object.fromEntries(entries));
  };
  useEffect(() => { load(); const t = setInterval(load, 9000); return () => clearInterval(t); }, []);

  const myOffer = (tid) => (offersByTow[tid] || []).find((o) => o.provider_id === user.id);

  const submitOffer = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tow-requests/${active.id}/offers`, {
        price: Number(of.price || 0), eta: of.eta, tow_type: of.tow_type,
        driver_name: of.driver_name, driver_phone: of.driver_phone,
      });
      toast.success("تم إرسال العرض للعميل");
      setActive(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (tid, status) => {
    const t = tows.find((x) => x.id === tid);
    // simulate driver moving toward pickup
    const lat = (t.from_lat || 24.71) + (Math.random() - 0.5) * 0.02;
    const lng = (t.from_lng || 46.67) + (Math.random() - 0.5) * 0.02;
    try { await api.patch(`/tow-requests/${tid}/status`, { status, lat, lng }); toast.success("تم تحديث الحالة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const available = tows.filter((t) => t.status === "requested" && (!cityFilter || t.city === cityFilter));
  const myJobs = tows.filter((t) => t.accepted_offer?.provider_id === user.id);

  const stats = [
    { label: "طلبات قريبة", value: available.length, icon: Truck, color: "text-accent bg-accent/10" },
    { label: "عروضي", value: Object.values(offersByTow).flat().filter((o) => o.provider_id === user.id).length, icon: Send, color: "text-violet-600 bg-violet-500/10" },
    { label: "مهامي النشطة", value: myJobs.filter((t) => t.status !== "delivered").length, icon: Navigation, color: "text-primary bg-primary/10" },
    { label: "أرباح محتملة", value: `${myJobs.reduce((s, t) => s + (t.accepted_offer?.price || 0), 0).toFixed(0)} ر.س`, icon: TrendingUp, color: "text-emerald-600 bg-emerald-500/10" },
  ];

  return (
    <Shell title={`مرحباً، ${user?.name}`} subtitle={`${user?.public_id ? `رقم معتمد: ${user.public_id} • ` : ""}استقبل طلبات السحب من جميع المناطق`} icon={Truck} accent="accent"
      actions={
        <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)} dir="rtl">
          <SelectTrigger className="w-48 rounded-full" data-testid="city-filter"><SelectValue placeholder="كل المدن" /></SelectTrigger>
          <SelectContent><SelectItem value="all">كل المدن</SelectItem>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      }>
      <PartnerEarnings />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <Card key={s.label} className="p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-6 h-6" /></div>
            <div><div className="text-2xl font-extrabold font-heading">{s.value}</div><div className="text-sm text-muted-foreground">{s.label}</div></div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="available" dir="rtl">
        <TabsList className="h-auto p-1">
          <TabsTrigger value="available" className="h-auto py-2" data-testid="tab-available">طلبات السحب</TabsTrigger>
          <TabsTrigger value="jobs" className="h-auto py-2" data-testid="tab-jobs">مهامي</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="mt-6">
          {available.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">لا توجد طلبات سحب قريبة الآن</div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {available.map((t) => {
                const mine = myOffer(t.id);
                return (
                  <Card key={t.id} className="p-5" data-testid={`tow-req-${t.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-bold flex items-center gap-2"><Truck className="w-4 h-4 text-accent" /> {t.vehicle_type}</div>
                      <Badge variant="outline">{t.tow_type}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-emerald-600" /> من: {t.from_location} ({t.city})</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5 text-accent" /> إلى: {t.to_location}</div>
                    {t.attachments?.length > 0 && (
                      <div className="grid grid-cols-5 gap-1.5 mb-2" data-testid={`tow-photos-${t.id}`}>
                        {t.attachments.map((a) => <div key={a.file_id} className="rounded-lg overflow-hidden border border-border"><ChatAttachment att={a} compact /></div>)}
                      </div>
                    )}
                    {t.linked_workshop && (
                      <div className="text-xs bg-primary/5 border border-primary/20 rounded-lg p-2 mb-2">
                        <div className="font-semibold text-primary flex items-center gap-1"><Navigation className="w-3.5 h-3.5" /> ورشة رقم {t.linked_workshop.public_id}: {t.linked_workshop.name}</div>
                        <div className="text-muted-foreground mt-0.5">{t.linked_workshop.address} — {t.linked_workshop.phone}</div>
                      </div>
                    )}
                    {t.from_lat && (
                      <MapView className="h-32 mb-2" center={[t.from_lat, t.from_lng]} zoom={12}
                        markers={[{ lat: t.from_lat, lng: t.from_lng, color: "#10B981", label: "موقع العميل" }]} />
                    )}
                    <div className="text-xs text-muted-foreground mb-3">الحالة: {t.drivable ? "تتحرك" : "متعطلة"} • الوقت: {t.pickup_time} • العميل: {t.customer?.name}</div>
                    {mine ? (
                      <Badge variant="outline" className="w-full justify-center py-2">عرضك: {mine.price} ر.س ({mine.status === "accepted" ? "مقبول" : mine.status === "rejected" ? "مرفوض" : "قيد المراجعة"})</Badge>
                    ) : (
                      <Dialog open={active?.id === t.id} onOpenChange={(o) => setActive(o ? t : null)}>
                        <DialogTrigger asChild>
                          <Button className="w-full rounded-xl gap-2 bg-accent hover:bg-accent/90" data-testid={`offer-btn-${t.id}`}><Send className="w-4 h-4" /> تقديم عرض</Button>
                        </DialogTrigger>
                        <DialogContent dir="rtl">
                          <DialogHeader><DialogTitle>عرض سحب</DialogTitle></DialogHeader>
                          <form onSubmit={submitOffer} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="mb-1.5 block">السعر (ر.س)</Label><Input type="number" value={of.price} onChange={(e) => setOf({ ...of, price: e.target.value })} required data-testid="o-price" /></div>
                              <div><Label className="mb-1.5 block">وقت الوصول</Label><Input value={of.eta} onChange={(e) => setOf({ ...of, eta: e.target.value })} required data-testid="o-eta" placeholder="مثال: 15 دقيقة" /></div>
                            </div>
                            <div><Label className="mb-1.5 block">اسم السائق</Label><Input value={of.driver_name} onChange={(e) => setOf({ ...of, driver_name: e.target.value })} required data-testid="o-driver" /></div>
                            <div><Label className="mb-1.5 block">رقم التواصل</Label><Input dir="ltr" value={of.driver_phone} onChange={(e) => setOf({ ...of, driver_phone: e.target.value })} required data-testid="o-phone" /></div>
                            <DialogFooter><Button type="submit" className="w-full rounded-xl bg-accent hover:bg-accent/90" data-testid="o-submit">إرسال العرض</Button></DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          {myJobs.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">لا توجد مهام مقبولة بعد</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {myJobs.map((t) => (
                <Card key={t.id} className="p-5" data-testid={`job-${t.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="font-bold">{t.vehicle_type} • {t.accepted_offer?.price} ر.س</div>
                    <Badge variant="outline" className={STATUS_COLORS[t.status]}>{STATUS_LABELS[t.status]}</Badge>
                  </div>
                  <MapView className="h-44 mb-3" markers={[
                    ...(t.from_lat ? [{ lat: t.from_lat, lng: t.from_lng, color: "#10B981", label: "المركبة" }] : []),
                    ...(t.driver_lat ? [{ lat: t.driver_lat, lng: t.driver_lng, color: "#F97316", label: "أنت" }] : []),
                  ]} />
                  <div className="mb-3"><StatusStepper statuses={TOW_STATUSES} current={t.status} accent="accent" /></div>
                  <Label className="mb-1.5 block text-sm">تحديث الحالة</Label>
                  {!t.paid ? (
                    <div className="text-sm text-amber-600 bg-amber-500/10 rounded-lg p-3 text-center" data-testid={`tow-await-pay-${t.id}`}>
                      بانتظار دفع العميل لقيمة السحب قبل تحديث الحالة
                    </div>
                  ) : (
                    <Select value={t.status} onValueChange={(v) => updateStatus(t.id, v)} dir="rtl">
                      <SelectTrigger data-testid={`tow-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TOW_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <div className="mt-4">
                    <div className="font-semibold text-sm mb-2">محادثة مع العميل</div>
                    <ChatBox threadType="tow" refId={t.id} meId={user.id} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
